<template>
  <form @submit="onSubmit" novalidate class="usa-form usa-form--large">
    <fieldset class="usa-fieldset">
      <p>
        Required fields are marked with an asterisk (<abbr
          title="required"
          class="usa-hint usa-hint--required"
        >*</abbr
        >).
      </p>

      <div :class="{ 'usa-form-group': true, 'usa-form-group--error': displayTypeError }">
        <label :class="{ 'usa-label': true, 'usa-label--error': displayTypeError }" for="contact_type">
          Inquiry type
          <abbr title="required" class="usa-hint usa-hint--required">*</abbr>
        </label>
        <span v-if="displayTypeError" class="usa-error-message">{{ typeError }}</span>
        <select
          v-model="type"
          @blur="typeHandleBlur"
          class="usa-select"
          id="contact_type"
          name="type"
          required
          aria-required="true"
        >
          <option value>- Select -</option>
          <option>General Technical Assistance</option>
          <option>Clean Heavy-Duty Vehicle Technical Assistance</option>
          <option>Clean School Bus Technical Assistance</option>
          <option>Tribal Nations Technical Assistance</option>
          <option>Reporting (EV-ChART)</option>
        </select>
      </div>

      <div :class="{ 'usa-form-group': true, 'usa-form-group--error': displayNameError }">
        <label :class="{ 'usa-label': true, 'usa-label--error': displayNameError }" for="contact_name">
          Name
          <abbr title="required" class="usa-hint usa-hint--required">*</abbr>
        </label>
        <span v-if="displayNameError" class="usa-error-message">{{ nameError }}</span>
        <input
          v-model="name"
          @blur="nameHandleBlur"
          class="usa-input"
          id="contact_name"
          name="name"
          type="text"
          required
          aria-required="true"
        />
      </div>

      <div :class="{ 'usa-form-group': true, 'usa-form-group--error': displayEmailError }">
        <label :class="{ 'usa-label': true, 'usa-label--error': displayEmailError }" for="contact_email">
          Email
          <abbr title="required" class="usa-hint usa-hint--required">*</abbr>
        </label>
        <span v-if="displayEmailError" class="usa-error-message">{{ emailError }}</span>
        <input
          v-model="email"
          @blur="emailHandleBlur"
          class="usa-input"
          id="contact_email"
          name="email"
          type="email"
          required
          aria-required="true"
        />
      </div>

      <div :class="{ 'usa-form-group': true, 'usa-form-group--error': displaySubjectError }">
        <label :class="{ 'usa-label': true, 'usa-label--error': displaySubjectError }" for="contact_subject">
          Subject
          <abbr title="required" class="usa-hint usa-hint--required">*</abbr>
        </label>
        <span v-if="displaySubjectError" class="usa-error-message">{{ subjectError }}</span>
        <input
          v-model="subject"
          @blur="subjectHandleBlur"
          class="usa-input"
          id="contact_subject"
          name="subject"
          type="text"
          required
          aria-required="true"
        />
      </div>

      <div :class="{ 'usa-form-group': true, 'usa-form-group--error': displayMessageError }">
        <label :class="{ 'usa-label': true, 'usa-label--error': displayMessageError }" for="contact_message">
          Message
          <abbr title="required" class="usa-hint usa-hint--required">*</abbr>
        </label>
        <span v-if="displayMessageError" class="usa-error-message">{{ messageError }}</span>
        <textarea
          v-model="message"
          @blur="messageHandleBlur"
          class="usa-textarea"
          id="contact_message"
          name="message"
          rows="6"
          required
          aria-required="true"
        ></textarea>
      </div>

      <button
        type="submit"
        class="usa-button"
        :disabled="isSubmitting"
      >
        {{ isSubmitting ? 'Sending...' : 'Send' }}
      </button>
    </fieldset>
  </form>
</template>

<script>
import { computed } from 'vue';
import { useForm, useField } from 'vee-validate';
import { object, string } from 'yup';
import Swal from 'sweetalert2/dist/sweetalert2';
import ky from 'ky';

export default {
  setup() {
    const schema = object({
      type: string().label('Inquiry type').required(),
      name: string().label('Name').required(),
      email: string().label('Email').required().email(),
      subject: string().label('Subject').required(),
      message: string().label('Message').required(),
    });

    const { handleSubmit, isSubmitting } = useForm({
      validationSchema: schema,
    });

    const onSubmit = handleSubmit(async (values, { resetForm }) => {
      const projectNames = {
        'Clean Heavy-Duty Vehicle Technical Assistance': 'clean_hd_vehicles',
        'Clean School Bus Technical Assistance': 'clean_school_bus',
        'Tribal Nations Technical Assistance': 'tribal_technical_assistance',
        'Reporting (EV-ChART)': 'evchart',
      };

      const projectName = projectNames[values.type] !== undefined ? projectNames[values.type] : 'joint_office_technical_assistance';

      const data = {
        contact: {
          ...values,
          project: projectName,
        },
      };

      delete data.contact.type;
      data.contact.subject = `[${values.type}] ${values.subject}`;

      try {
        await ky.post(import.meta.env.VITE_CONTACTOR_API_URL, {
          searchParams: {
            api_key: import.meta.env.VITE_NREL_API_KEY,
          },
          json: data,
        });

        resetForm();

        Swal.fire({
          text: "Thanks for sending your message. We'll be in touch.",
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);

        Swal.fire({
          text: 'Sending your message unexpectedly failed. Please try again.',
        });
      }
    });

    const {
      value: type,
      errorMessage: typeError,
      handleBlur: typeHandleBlur,
      meta: typeMeta,
    } = useField('type');
    const {
      value: name,
      errorMessage: nameError,
      handleBlur: nameHandleBlur,
      meta: nameMeta,
    } = useField('name');
    const {
      value: email,
      errorMessage: emailError,
      handleBlur: emailHandleBlur,
      meta: emailMeta,
    } = useField('email');
    const {
      value: subject,
      errorMessage: subjectError,
      handleBlur: subjectHandleBlur,
      meta: subjectMeta,
    } = useField('subject');
    const {
      value: message,
      errorMessage: messageError,
      handleBlur: messageHandleBlur,
      meta: messageMeta,
    } = useField('message');

    const displayTypeError = computed(() => typeError.value && typeMeta.touched);
    const displayNameError = computed(() => nameError.value && nameMeta.touched);
    const displayEmailError = computed(() => emailError.value && emailMeta.touched);
    const displaySubjectError = computed(() => subjectError.value && subjectMeta.touched);
    const displayMessageError = computed(() => messageError.value && messageMeta.touched);

    if (window.location.search.includes('inquiry=general')) {
      type.value = 'General Technical Assistance';
    } else if (window.location.search.includes('inquiry=hdvehicle')) {
      type.value = 'Clean Heavy-Duty Vehicle Technical Assistance';
    } else if (window.location.search.includes('inquiry=schoolbus')) {
      type.value = 'Clean School Bus Technical Assistance';
    } else if (window.location.search.includes('inquiry=tribal')) {
      type.value = 'Tribal Nations Technical Assistance';
    } else if (window.location.search.includes('inquiry=evchart')) {
      type.value = 'Reporting (EV-ChART)';
    }

    return {
      type,
      typeError,
      typeHandleBlur,
      displayTypeError,
      name,
      nameError,
      nameHandleBlur,
      displayNameError,
      email,
      emailError,
      emailHandleBlur,
      displayEmailError,
      subject,
      subjectError,
      subjectHandleBlur,
      displaySubjectError,
      message,
      messageError,
      messageHandleBlur,
      displayMessageError,
      isSubmitting,
      onSubmit,
    };
  },
};
</script>
